import { api, type RequestOptions } from './base';

export type MailboxSmtpMode = 'shared' | 'own';

export const MAIL_POLICY_EVENTS = [
  'ticket_created',
  'ticket_in_progress',
  'staff_reply',
  'waiting_for_requester',
  'resolution_requested',
  'ticket_closed',
  'ticket_rejected',
  'status_changed',
] as const;
export type MailPolicyEvent = (typeof MAIL_POLICY_EVENTS)[number];

export interface MailboxSettings {
  policy: Record<MailPolicyEvent, boolean>;
  fromMode: 'mailbox' | 'shared';
  plusAddressing: boolean;
  signature: string;
  engineerName: 'mailbox' | 'real';
  sameSender: 'attach' | 'suggest' | 'off';
  sameSenderDays: number;
  ccAsParticipants: boolean;
  closedTicket: 'new_ticket' | 'notify';
  quarantineOnAuthFail: boolean;
  senderHourlyCap: number;
  maxAttachMb: number;
  allowInvalidCert: boolean;
  allowedSenderDomains: string[];
  inlineImageSkipKb: number;
  clientMode: MailClientMode;
  routingRules: MailRoutingRule[];
  appearance: MailAppearance;
}

export type MailPresetKey = 'indigo' | 'emerald' | 'coral' | 'graphite' | 'ocean' | 'plum' | 'amber' | 'teal';
export type MailLayout = 'bubbles' | 'thread' | 'card';
export type MailHeader = 'banner' | 'minimal' | 'centered';
export type MailFont = 'system' | 'grotesk' | 'serif' | 'rounded';
export type MailShape = 'round' | 'soft' | 'square';

export interface MailAppearance {
  preset: MailPresetKey | null;
  accent: string | null;
  layout: MailLayout | null;
  header: MailHeader | null;
  font: MailFont | null;
  shape: MailShape | null;
  logo: string | null;
  brandName: string;
}

export type MailClientMode = 'fixed' | 'sender';

export type MailRoutingMatch = 'any' | 'all' | 'phrase' | 'regex';

export interface MailRoutingRule {
  id: string;
  name: string;
  enabled: boolean;
  match: MailRoutingMatch;
  where: 'subject' | 'subject_body';
  keywords: string[];
  categoryId: string | null;
  subcategoryId: string | null;
  groupId: string | null;
}

export interface Mailbox {
  id: string;
  clientId: string | null;
  clientName: string | null;
  address: string;
  displayName: string;
  enabled: boolean;
  imap: {
    host: string | null;
    port: number | null;
    secure: boolean;
    user: string | null;
    hasPassword: boolean;
    folder: string;
    processedFolder: string | null;
    configured: boolean;
  };
  smtp: {
    mode: MailboxSmtpMode;
    host: string | null;
    port: number | null;
    secure: boolean;
    user: string | null;
    hasPassword: boolean;
  };
  catalogId: string | null;
  categoryId: string | null;
  subcategoryId: string | null;
  groupId: string | null;
  workflowId: string | null;
  settings: MailboxSettings;
  lastPollAt: string | null;
  lastPollError: string | null;
  archivedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface MailboxServerInput {
  host?: string | null;
  port?: number | null;
  secure?: boolean;
  user?: string | null;
  password?: string | null;
}

export interface MailboxInput {
  clientId?: string | null;
  address?: string;
  displayName?: string;
  enabled?: boolean;
  imap?: MailboxServerInput & { folder?: string; processedFolder?: string | null };
  smtp?: MailboxServerInput & { mode?: MailboxSmtpMode };
  catalogId?: string | null;
  categoryId?: string | null;
  subcategoryId?: string | null;
  groupId?: string | null;
  workflowId?: string | null;
  settings?: Partial<MailboxSettings>;
}

export interface MailConnectionCheck {
  ok: boolean;
  message: string;
  details?: { folders?: string[] };
}

export interface MailGlobalSettings {
  approvalButtons: boolean;
  tokenTtlHours: number;
  pollEnabled: boolean;
}

export type MailTemplateKey =
  | 'ticket_created'
  | 'ticket_in_progress'
  | 'staff_reply'
  | 'waiting_for_requester'
  | 'resolution_requested'
  | 'ticket_closed'
  | 'ticket_rejected'
  | 'status_changed'
  | 'closed_ticket_notice'
  | 'approval_requested';

export interface MailTemplateVariable {
  key: string;
  label: string;
  sample: string;
  html?: boolean;
}

export interface MailTemplateContent {
  subject: string;
  heading: string;
  bodyHtml: string;
}

export interface MailTemplate extends MailTemplateContent {
  key: MailTemplateKey;
  mailboxId: string | null;
  source: 'default' | 'global' | 'mailbox';
  blocks: unknown[] | null;
  updatedAt: string | null;
  label: string;
  hint: string;
  audience: 'requester' | 'approver';
  actions: { key: string; label: string }[];
  variables: MailTemplateVariable[];
  defaults: MailTemplateContent;
}

export interface MailPreview {
  subject: string;
  html: string;
  text: string;
}

export type InboundStatus = 'processing' | 'ticket_created' | 'message_added' | 'ignored' | 'quarantined' | 'failed';
export type ThreadMatch = 'headers' | 'reply_token' | 'subject_number' | 'quote_ref' | 'same_sender' | 'manual' | 'none';
export type OutboundStatus = 'queued' | 'sending' | 'sent' | 'failed' | 'bounced';

export interface MailTicketRef {
  id: string;
  number: number | null;
  subject: string;
}

export interface InboundListItem {
  id: string;
  mailboxId: string | null;
  mailboxAddress: string | null;
  from: string | null;
  fromName: string | null;
  subject: string;
  receivedAt: string | null;
  status: InboundStatus;
  reason: string | null;
  threadMatch: ThreadMatch | null;
  authResult: 'pass' | 'fail' | 'none';
  ticket: MailTicketRef | null;
  attachmentsCount: number;
}

export interface InboundAttachmentInfo {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  status: 'saved' | 'skipped' | 'rejected';
  reason?: string;
  attachmentId?: string;
}

export interface InboundEmailDetail {
  id: string;
  mailboxId: string | null;
  messageId: string;
  from: string | null;
  fromName: string | null;
  to: string[];
  cc: string[];
  subject: string;
  sentAt: string | null;
  receivedAt: string | null;
  textBody: string;
  cleanBody: string;
  attachments: InboundAttachmentInfo[];
  authResult: 'pass' | 'fail' | 'none';
  status: InboundStatus;
  reason: string | null;
  threadMatch: ThreadMatch | null;
  ticketId: string | null;
  ticketMessageId: string | null;
  suggestedTicketId: string | null;
  hasRaw: boolean;
  ticket?: MailTicketRef | null;
  suggestedTicket?: MailTicketRef | null;
}

export interface OutboundListItem {
  id: string;
  mailboxId: string | null;
  mailboxAddress: string | null;
  kind: MailTemplateKey | string;
  to: string[];
  cc: string[];
  subject: string;
  status: OutboundStatus;
  error: string | null;
  attempts: number;
  createdAt: string | null;
  sentAt: string | null;
  ticket: MailTicketRef | null;
}

export interface OutboundDetail {
  id: string;
  kind: string;
  from: string;
  replyTo: string | null;
  to: string[];
  cc: string[];
  subject: string;
  html: string;
  status: OutboundStatus;
  error: string | null;
  createdAt: string | null;
  sentAt: string | null;
  ticketId: string | null;
}

export interface MailJournalQuery {
  mailboxId?: string;
  status?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface MailJournalPage<T> {
  data: T[];
  meta: { total: number; page: number; pageSize: number };
}

export interface MessageMailInfo {
  inbound?: {
    id: string;
    from: string | null;
    fromName: string | null;
    sentAt: string | null;
    authResult: string;
    rejectedFiles: { fileName: string; reason: string | null }[];
  };
  outbound: { id: string; status: OutboundStatus; error: string | null; sentAt: string | null; to: string[]; cc: string[] }[];
}

export interface TicketMailInfo {
  channel: string;
  mailbox: { id: string; address: string; displayName: string; enabled: boolean } | null;
  requester: { id: string; address: string | null; isContact: boolean; undeliverable: boolean } | null;
  cc: { id: string; address: string }[];
  canSendEmail: boolean;
  cannotSendReason: string | null;
  messages: Record<string, MessageMailInfo>;
  suggestion: MailTicketRef & { ticketId: string } | null;
}

export type MailActionState = 'ready' | 'used' | 'expired' | 'stale';

export interface MailActionView {
  state: MailActionState;
  action: 'confirm_resolution' | 'reject_resolution' | 'approve' | 'reject_approval' | string;
  label: string;
  needsComment: 'never' | 'always' | 'low_rating';
  needsRating: boolean;
  ticket: { number: number | null; subject: string; statusLabel: string };
  usedAt: string | null;
  expiresAt: string;
  outcome: {
    ticketClosed: boolean;
    rating: number | null;
    ratedAt: string | null;
    approval: 'approved' | 'rejected' | 'pending' | null;
    approvalDecidedAt: string | null;
  };
}

const A = '/api/v1/admin/mail';

function journalParams(q: MailJournalQuery): Record<string, string | number | undefined> {
  return { mailboxId: q.mailboxId, status: q.status, q: q.q, page: q.page, pageSize: q.pageSize };
}

export const mailApi = {
  listMailboxes: (params?: { clientId?: string; archived?: boolean }) =>
    api.get<Mailbox[]>(`${A}/mailboxes`, { clientId: params?.clientId, archived: params?.archived ? '1' : undefined }),
  getMailbox: (id: string) => api.get<Mailbox>(`${A}/mailboxes/${id}`),
  createMailbox: (input: MailboxInput) => api.post<Mailbox>(`${A}/mailboxes`, input),
  updateMailbox: (id: string, input: MailboxInput) => api.patch<Mailbox>(`${A}/mailboxes/${id}`, input),
  archiveMailbox: (id: string) => api.delete<unknown>(`${A}/mailboxes/${id}`),
  checkMailbox: (id: string) =>
    api.post<{ imap: MailConnectionCheck; smtp: MailConnectionCheck }>(`${A}/mailboxes/${id}/check`, {}),
  pollMailbox: (id: string) =>
    api.post<{ mailboxId: string; fetched: number; error: string | null }>(`${A}/mailboxes/${id}/poll`, {}),
  testMailbox: (id: string, to: string) => api.post<{ ok: boolean }>(`${A}/mailboxes/${id}/test`, { to }),

  getSettings: () => api.get<MailGlobalSettings>(`${A}/settings`),
  updateSettings: (patch: Partial<MailGlobalSettings>) => api.put<MailGlobalSettings>(`${A}/settings`, patch),

  listTemplates: (mailboxId?: string | null) =>
    api.get<MailTemplate[]>(`${A}/templates`, { mailboxId: mailboxId ?? undefined }),
  saveTemplate: (key: MailTemplateKey, body: MailTemplateContent & { mailboxId?: string | null; blocks?: unknown[] | null }) =>
    api.put<MailTemplate>(`${A}/templates/${key}`, body),
  resetTemplate: (key: MailTemplateKey, mailboxId?: string | null) =>
    api.delete<MailTemplate>(`${A}/templates/${key}${mailboxId ? `?mailboxId=${encodeURIComponent(mailboxId)}` : ''}`),
  previewTemplate: (
    key: MailTemplateKey,
    body: Partial<MailTemplateContent> & { mailboxId?: string | null; appearance?: MailAppearance },
  ) => api.post<MailPreview>(`${A}/templates/${key}/preview`, body),
  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.postForm<{ logo: string }>(`${A}/logos`, form);
  },
  testTemplate: (key: MailTemplateKey, body: Partial<MailTemplateContent> & { mailboxId?: string | null; to: string }) =>
    api.post<{ ok: boolean }>(`${A}/templates/${key}/test`, body),

  listInbound: (q: MailJournalQuery) =>
    api.get<InboundListItem[]>(`${A}/inbound`, journalParams(q)) as Promise<MailJournalPage<InboundListItem>>,
  getInbound: (id: string) => api.get<InboundEmailDetail>(`${A}/inbound/${id}`),
  processInbound: (id: string, body: { mode: 'new' } | { mode: 'attach'; ticketId: string }) =>
    api.post<InboundEmailDetail>(`${A}/inbound/${id}/process`, body),
  listOutbound: (q: MailJournalQuery) =>
    api.get<OutboundListItem[]>(`${A}/outbound`, journalParams(q)) as Promise<MailJournalPage<OutboundListItem>>,
  getOutbound: (id: string) => api.get<OutboundDetail>(`${A}/outbound/${id}`),
  resendOutbound: (id: string) => api.post<{ ok: boolean }>(`${A}/outbound/${id}/resend`, {}),

  ticketMail: (ticketId: string) => api.get<TicketMailInfo>(`/api/v1/tickets/${ticketId}/mail`),
  sendMessageAsEmail: (ticketId: string, messageId: string, resend = false) =>
    api.post<{ outboundEmailId: string; to: string[]; cc: string[] }>(
      `/api/v1/tickets/${ticketId}/messages/${messageId}/email`,
      resend ? { resend } : {},
    ),
  originalEmail: (ticketId: string, messageId: string) =>
    api.get<InboundEmailDetail>(`/api/v1/tickets/${ticketId}/messages/${messageId}/original`),
  moveMessage: (ticketId: string, messageId: string, targetTicketId: string) =>
    api.post<{ ok: boolean }>(`/api/v1/tickets/${ticketId}/messages/${messageId}/move`, { targetTicketId }),

  viewAction: (token: string, opts?: RequestOptions) =>
    api.get<MailActionView>(`/api/v1/mail-actions/${encodeURIComponent(token)}`, undefined, opts),
  performAction: (token: string, body: { rating?: number; comment?: string | null }, opts?: RequestOptions) =>
    api.post<{ message: string }>(`/api/v1/mail-actions/${encodeURIComponent(token)}`, body, opts),
};
